import './applicantPage.scss';

import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import heroWithCamera from '../../../assets/hero-with-camera.svg';
import logo from '../../../assets/logo-default.svg';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import useDashboardStore from '../../../store/dashboardStore/useDashboardStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { setDrawerOpen } = useNavigationPaneStore();
  const { next } = useWorkflowStore();
  const { handleSubmit } = useForm();

  const {
    computed: { vehicleYearMakeModel },
  } = useDashboardStore();

  const formSubmit = (): void => {
    navigate(next());
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="applicant-page" sx={theme.pageLayout}>
        <header data-testid={locator.pages.applicant.header.root}>
          <img alt={t('imageAlts.logo')} src={logo} />
          <button
            aria-label="Toggle drawer"
            data-testid={locator.pages.applicant.header.drawerButton}
            type="button"
            onClick={() => {
              setDrawerOpen(true);
            }}
          >
            <MenuRoundedIcon />
          </button>
        </header>

        <div className="progress-container" role="complementary">
          <LinearProgress
            aria-label="Application completed percentage"
            data-testid={locator.pages.applicant.progress.root}
            value={useWorkflowStore.getState().computed.progress}
            variant="determinate"
          />
        </div>

        <main>
          <figure>
            <img alt={t('imageAlts.heroWithCamera')} src={heroWithCamera} />
          </figure>
          <form
            data-testid={locator.pages.applicant.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.applicant.form.h1}>
                {t('ApplicantPage.title', {
                  vehicle: vehicleYearMakeModel,
                })}
              </h1>
              <h2 data-testid={locator.pages.applicant.form.h2}>
                {t('ApplicantPage.subtitle')}
              </h2>

              <div style={{ marginBottom: '3rem' }}>
                <LoadingButton
                  color="primary"
                  data-testid={locator.pages.applicant.form.submitButton}
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('ApplicantPage.continue')}
                </LoadingButton>
              </div>
            </div>
            <footer data-testid={locator.pages.applicant.footer.root}>
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicantPage';
